import { Fragment, useRef } from "react"
import { useTranslation } from "react-i18next"
import { useParams } from "react-router-dom"
import { SlidePanel, Button, CarrierName } from "../../../components"
import { StatusPill } from "../../../components/BasicTable/StatusPill"
import { ResponseBodyViewer } from "./ResponseBodyViewer"

const T_PREFIX = "lastmile_management.automated_coverage_deactivations"

const STATUS_DEFS = (i18n) => [
    {
        status: "pending",
        label: i18n.t(`${T_PREFIX}.status.pending`),
        classes: "bg-yellow-100 text-yellow-800 border-yellow-200",
    },
    {
        status: "acknowledged",
        label: i18n.t(`${T_PREFIX}.status.acknowledged`),
        classes: "bg-green-100 text-green-800 border-green-200",
    },
]

const formatDate = (iso) => {
    if (!iso) return "—"
    try {
        return new Date(iso).toLocaleString()
    } catch (e) {
        return iso
    }
}

const Field = ({ label, children }) => (
    <div className="grid grid-cols-[140px_1fr] gap-3 py-1.5 text-sm items-start">
        <span className="text-gray-500">{label}</span>
        <span className="text-gray-900 break-words">{children ?? "—"}</span>
    </div>
)

const Section = ({ title, children }) => (
    <section className="mb-5 bg-white border border-gray-200 rounded-lg overflow-hidden">
        <h3 className="px-4 py-2 text-xs font-semibold uppercase tracking-wide text-gray-600 bg-gray-50 border-b border-gray-200">
            {title}
        </h3>
        <div className="px-4 py-3">
            {children}
        </div>
    </section>
)

export const DeactivationDetailDrawer = ({
    deactivation,
    onClose,
    onAcknowledge,
    isAcknowledging,
    referrer,
}) => {
    const { i18n } = useTranslation()
    const { warehouse_id } = useParams()
    const slidePanelRef = useRef()

    if (!deactivation) {
        return null
    }

    const isAcknowledged = !!deactivation.acknowledged_at
    const acknowledgedByEmail =
        deactivation.acknowledged_by?.email ||
        (deactivation.acknowledged_by_id ? `ID #${deactivation.acknowledged_by_id}` : null)

    const sm = deactivation.shipping_method
    const sc = deactivation.shipping_coverage
    const fe = deactivation.fulfillment_error

    const orderHref = fe?.order_id
        ? `/warehouse/${warehouse_id}/orders/show/${fe.order_id}`
        : null

    const handleClose = () => {
        slidePanelRef.current.closePanel()
    }

    const renderBoolean = (value) =>
        value
            ? i18n.t("lastmile_management.boolean_yes")
            : i18n.t("lastmile_management.boolean_no")

    return (
        <SlidePanel
            ref={slidePanelRef}
            title={i18n.t(`${T_PREFIX}.drawer.title`)}
            onClose={onClose}
            referrer={referrer}
        >
            <div className="flex flex-col h-full bg-gray-50">
                <div className="flex-1 overflow-auto px-6 py-5">

                    {/* Status header */}
                    <div className="mb-5 flex items-center gap-3">
                        <StatusPill
                            status={isAcknowledged ? "acknowledged" : "pending"}
                            statuses={STATUS_DEFS(i18n)}
                        />
                        {isAcknowledged && acknowledgedByEmail && (
                            <span className="text-xs text-gray-500">
                                {formatDate(deactivation.acknowledged_at)} · {acknowledgedByEmail}
                            </span>
                        )}
                    </div>

                    {/* Warning (only when pending) */}
                    {!isAcknowledged && (
                        <div className="mb-5 bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                            <p className="text-sm text-yellow-900">
                                ⚠ {i18n.t(`${T_PREFIX}.drawer.warning`)}
                            </p>
                        </div>
                    )}

                    {/* Información del envío */}
                    <Section title={i18n.t(`${T_PREFIX}.drawer.shipping`)}>
                        {sm ? (
                            <Fragment>
                                <Field label={i18n.t(`${T_PREFIX}.columns.carrier`)}>
                                    <CarrierName preferInternal carrierName={sm.carrier_name} internalCarrierName={sm.internal_carrier_name} />
                                </Field>
                                <Field label={i18n.t(`${T_PREFIX}.columns.shippingMethod`)}>
                                    {sm.shipping_name}
                                </Field>
                                <Field label={i18n.t(`${T_PREFIX}.drawer.shippingType`)}>
                                    {[sm.shipping_type, sm.delivery_type].filter(Boolean).join(" / ") || "—"}
                                </Field>
                            </Fragment>
                        ) : (
                            <p className="text-sm text-gray-500 italic">
                                {i18n.t(`${T_PREFIX}.drawer.shippingMethodUnavailable`)}
                            </p>
                        )}
                    </Section>

                    {/* Cobertura desactivada */}
                    <Section title={i18n.t(`${T_PREFIX}.drawer.coverage`)}>
                        {sc ? (
                            <Fragment>
                                <Field label={i18n.t(`${T_PREFIX}.columns.zipCode`)}>
                                    {sc.value}
                                </Field>
                                <Field label={i18n.t(`${T_PREFIX}.drawer.zone`)}>
                                    {sc.zone}
                                </Field>
                                <Field label={i18n.t(`${T_PREFIX}.drawer.enabled`)}>
                                    <div className="flex items-center gap-2">
                                        <span>{renderBoolean(sc.enabled)}</span>
                                        {sc.enabled && (
                                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                                                {i18n.t(`${T_PREFIX}.drawer.coverageReenabled`)}
                                            </span>
                                        )}
                                    </div>
                                </Field>
                                <Field label={i18n.t(`${T_PREFIX}.drawer.extendedZone`)}>
                                    {renderBoolean(sc.extended_zone)}
                                </Field>
                                <Field label={i18n.t(`${T_PREFIX}.drawer.poBoxDropoff`)}>
                                    {renderBoolean(sc.po_box_dropoff)}
                                </Field>
                            </Fragment>
                        ) : (
                            <p className="text-sm text-gray-500 italic">
                                {i18n.t(`${T_PREFIX}.drawer.coverageUnavailable`)}
                            </p>
                        )}
                    </Section>

                    {/* Error que disparó */}
                    <Section title={i18n.t(`${T_PREFIX}.drawer.error`)}>
                        {fe ? (
                            <Fragment>
                                <Field label={i18n.t(`${T_PREFIX}.columns.order`)}>
                                    {orderHref ? (
                                        <a
                                            href={orderHref}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-blue-600 hover:underline"
                                        >
                                            #{fe.order_id}
                                        </a>
                                    ) : (
                                        <span className="text-gray-500 italic">
                                            {i18n.t(`${T_PREFIX}.drawer.noOrder`)}
                                        </span>
                                    )}
                                </Field>
                                <Field label={i18n.t(`${T_PREFIX}.drawer.errorCode`)}>
                                    <span className="font-mono text-xs">{fe.code}</span>
                                </Field>
                                <div className="mt-3">
                                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                                        {i18n.t(`${T_PREFIX}.columns.message`)}
                                    </p>
                                    <p className="text-sm text-gray-900 whitespace-pre-wrap break-words bg-gray-50 border border-gray-200 rounded p-2">
                                        {fe.error_message}
                                    </p>
                                </div>
                                <div className="mt-3">
                                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                                        {i18n.t(`${T_PREFIX}.drawer.responseBody`)}
                                    </p>
                                    <ResponseBodyViewer
                                        metadata={fe.metadata}
                                        errorMessage={fe.error_message}
                                    />
                                </div>
                            </Fragment>
                        ) : (
                            <p className="text-sm text-gray-500 italic">
                                {i18n.t(`${T_PREFIX}.drawer.noError`)}
                            </p>
                        )}
                    </Section>

                    {/* Fechas */}
                    <Section title={i18n.t(`${T_PREFIX}.drawer.dates`)}>
                        <Field label={i18n.t(`${T_PREFIX}.drawer.deactivatedAt`)}>
                            {formatDate(deactivation.created_at)}
                        </Field>
                        <Field label={i18n.t(`${T_PREFIX}.drawer.acknowledgedAt`)}>
                            {isAcknowledged ? (
                                <span>
                                    {formatDate(deactivation.acknowledged_at)}
                                    {acknowledgedByEmail && (
                                        <span className="text-gray-500"> · {acknowledgedByEmail}</span>
                                    )}
                                </span>
                            ) : (
                                "—"
                            )}
                        </Field>
                    </Section>
                </div>

                {/* Sticky footer */}
                <div className="border-t border-gray-200 bg-white px-6 py-3 flex justify-end gap-2 shadow-lg">
                    <Button onClick={handleClose} type="secondary">
                        {i18n.t(`${T_PREFIX}.drawer.close`)}
                    </Button>
                    {!isAcknowledged && (
                        <Button
                            onClick={() => onAcknowledge(deactivation.id)}
                            disabled={isAcknowledging}
                        >
                            {i18n.t(`${T_PREFIX}.actions.acknowledge`)}
                        </Button>
                    )}
                </div>
            </div>
        </SlidePanel>
    )
}
